import { type FC } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { drisAnalysisColors } from '../../theme/colors';

interface SampleTypeIndicatorProps {
  /** Whether Sap samples are expected (> 0) */
  hasSap: boolean;
  /** Whether Tissue samples are expected (> 0) */
  hasTissue: boolean;
  /** Whether Soil samples are expected (> 0) */
  hasSoil: boolean;
  /** Size variant */
  size?: 'small' | 'medium';
}

const INACTIVE_COLOR = '#BDBDBD';

interface LetterConfig {
  letter: string;
  label: string;
  activeColor: string;
  active: boolean;
}

/**
 * Compact combined indicator showing which sample types (Sap, Tissue, Soil)
 * are present in a workflow. Active types display in their brand color,
 * inactive types are greyed out.
 *
 * Renders as: S T S (Sap · Tissue · Soil)
 */
export const SampleTypeIndicator: FC<SampleTypeIndicatorProps> = ({
  hasSap,
  hasTissue,
  hasSoil,
  size = 'small',
}) => {
  const letters: LetterConfig[] = [
    { letter: 'S', label: 'Sap', activeColor: drisAnalysisColors.sap, active: hasSap },
    { letter: 'T', label: 'Tissue', activeColor: drisAnalysisColors.tissue, active: hasTissue },
    { letter: 'S', label: 'Soil', activeColor: drisAnalysisColors.soil, active: hasSoil },
  ];

  const activeLabels = letters.filter((l) => l.active).map((l) => l.label);
  const tooltipText = activeLabels.length > 0
    ? activeLabels.join(', ')
    : 'No sample types';

  const isSmall = size === 'small';
  const fontSize = isSmall ? '0.65rem' : '0.75rem';
  const boxHeight = isSmall ? 20 : 24;
  const letterWidth = isSmall ? 16 : 20;

  return (
    <Tooltip title={tooltipText} arrow>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          height: boxHeight,
          borderRadius: '10px',
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          cursor: 'default',
        }}
      >
        {letters.map((item, index) => (
          <Box
            key={`${item.label}-${index}`}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: letterWidth,
              height: '100%',
              bgcolor: item.active ? item.activeColor : 'transparent',
              // Subtle divider between letters
              borderRight: index < letters.length - 1 ? '1px solid' : 'none',
              borderRightColor: 'divider',
            }}
          >
            <Typography
              sx={{
                fontSize,
                fontWeight: 700,
                lineHeight: 1,
                color: item.active ? '#fff' : INACTIVE_COLOR,
                userSelect: 'none',
              }}
            >
              {item.letter}
            </Typography>
          </Box>
        ))}
      </Box>
    </Tooltip>
  );
};
